import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { resolveImageUrl } from '../../lib/utils';
import { cardImgProps } from '../../lib/images';

const EASE = [0.22, 1, 0.36, 1];

/* Mills and brands we buy from — a logo if one has been uploaded in admin,
   otherwise a clean typographic wordmark so the wall is never broken. */
export const PartnerWall = ({ partners }) => {
  const { t } = useLanguage();
  if (!partners.length) return null;

  return (
    <section className="bg-[var(--px-paper)] border-b px-hair py-20 md:py-28" data-testid="partner-wall">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-24 items-end mb-12">
          <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)]">
            [ 01 ] {t('home.partnersKicker')}
          </p>
          <div className="max-w-3xl">
            <h2 className="px-display text-2xl md:text-4xl leading-tight font-semibold text-[var(--px-ink)]">
              {t('home.partnersHeading')}
            </h2>
            <p className="text-sm text-[var(--px-ink-soft)] mt-4 leading-relaxed max-w-xl">
              {t('home.partnersText')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-t border-s px-hair">
          {partners.map((p, i) => (
            <motion.div
              key={p.id || p.name}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-5%' }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 10) * 0.04 }}
              data-testid={`partner-${i}`}
              className="group relative border-b border-e px-hair bg-[var(--px-surface)] h-[118px] md:h-[132px] flex flex-col items-center justify-center px-4 text-center transition-colors duration-500 hover:bg-[var(--px-paper)]"
            >
              {p.logo ? (
                <img
                  src={resolveImageUrl(p.logo)}
                  alt={p.name}
                  loading="lazy"
                  className="max-h-9 md:max-h-11 w-auto object-contain grayscale opacity-70 transition-all duration-500 group-hover:grayscale-0 group-hover:opacity-100"
                />
              ) : (
                <span className="px-display text-sm md:text-base font-bold uppercase tracking-[0.12em] text-[var(--px-ink)] leading-tight transition-colors duration-500 group-hover:text-[var(--px-accent)]">
                  {p.name}
                </span>
              )}
              {p.note && (
                <span className="px-data text-[9px] tracking-[0.14em] uppercase text-[var(--px-ink-soft)] mt-3 leading-snug line-clamp-2">
                  {p.note}
                </span>
              )}
              {p.relationship === 'distributor' && (
                <span
                  data-testid={`partner-${i}-distributor`}
                  className="px-data absolute top-2 end-2 px-pill bg-[var(--px-accent)] text-white text-[8px] tracking-[0.14em] uppercase px-2 py-[3px]"
                >
                  {t('home.partnerDistributor')}
                </span>
              )}
            </motion.div>
          ))}
        </div>

        <p className="text-[11px] md:text-xs text-[var(--px-ink-soft)] leading-relaxed mt-8 max-w-4xl" data-testid="partner-disclaimer">
          {t('home.partnerDisclaimer')}{' '}
          <Link to="/legal/trademarks" data-testid="trademark-notice-link" className="text-[var(--px-ink)] underline underline-offset-2 hover:text-[var(--px-accent)] transition-colors">
            {t('home.trademarkNotice')}
          </Link>
        </p>
      </div>
    </section>
  );
};

/* Client logos are only fetched from a permission-gated endpoint, so this
   section simply does not render until a signed-off logo exists. */
export const ClientWall = ({ clients }) => {
  const { t } = useLanguage();
  if (!clients.length) return null;

  return (
    <section className="bg-[var(--px-surface)] border-b px-hair py-16 md:py-24" data-testid="client-wall">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)] mb-10">
          {t('home.clientsKicker')}
        </p>
        <div className="flex flex-wrap items-center gap-x-12 gap-y-10">
          {clients.map((c, i) => (
            <motion.div
              key={c.id || c.name}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-5%' }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 8) * 0.05 }}
              data-testid={`client-${i}`}
              className="h-12 md:h-14 flex items-center"
              title={c.industry || c.name}
            >
              <img
                src={resolveImageUrl(c.logo)}
                alt={c.name}
                loading="lazy"
                className="max-h-full w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
              />
            </motion.div>
          ))}
        </div>
        <p className="text-[11px] text-[var(--px-ink-soft)] mt-8 max-w-3xl leading-relaxed">
          {t('home.clientsDisclaimer')}
        </p>
      </div>
    </section>
  );
};

const IMG = 'https://static.prod-images.emergentagent.com/jobs/63829e06-ac96-4b28-aaaa-298082f37e5c/images';

const INDUSTRIES = [
  { key: 'oilgas', img: `${IMG}/ffbc7c7a198811b73e08b0af989c73eae270cc10f2e4be93b8613a55615db06f.jpeg` },
  { key: 'petrochemical', img: `${IMG}/e0f80f66d474eeaa83efc8068ae0d8af423f40c009ae90f89353460c0743aceb.jpeg` },
  { key: 'cement', img: `${IMG}/1d54a405e522ef870a1ece778f4b36e97cc908792686ebe7db4c85bf0d03a970.jpeg` },
  { key: 'mining', img: `${IMG}/9a3be88fcb0b2ced6d39bb2eca521e6b490486e5a3b9cab69487ac06017b2861.jpeg` },
  { key: 'pharma', img: `${IMG}/bb4582f4bca90b786661cf83d523d03485cf91367da093219aa62cc204a16a17.jpeg` },
  { key: 'desal', img: `${IMG}/1489ca0c1b7d1101c11700d2c38fae142fe8ac7f5c10859d26dcc0e732a42bb0.jpeg` },
  { key: 'marine', img: `${IMG}/37fdf379d9324a5ad1e1fddd84bf5e911903200f53d59a3290d1caef7adb0b06.jpeg` },
  { key: 'defence', img: `${IMG}/27faef023d56c089fc26509dfd3071223ea9789aec4e5358076e6308c50c47f4.jpeg` },
  { key: 'fabrication', img: `${IMG}/fc52d370c43b0d19c561f87874aa3b7b80dbc21de54f95c6a6afdcc50d773af2.jpeg` },
];

export const INDUSTRY_IMAGES = Object.fromEntries(INDUSTRIES.map(i => [i.key, i.img]));

export const IndustryWall = () => {
  const { t } = useLanguage();

  return (
    <section className="bg-[var(--px-surface)] py-20 md:py-32" data-testid="industry-wall">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-24 items-end mb-12 md:mb-16">
          <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)]">
            [ 02 ] {t('home.industriesKicker')}
          </p>
          <h2 className="px-display text-2xl md:text-4xl leading-tight font-semibold text-[var(--px-ink)] max-w-3xl">
            {t('home.industriesHeading')}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {INDUSTRIES.map((ind, i) => (
            <motion.a
              key={ind.key}
              href={`/industries/${ind.key}`}
              data-testid={`industry-${ind.key}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-6%' }}
              transition={{ duration: 0.8, ease: EASE, delay: (i % 3) * 0.08 }}
              className="group relative overflow-hidden rounded-lg bg-[var(--px-ink)] aspect-[4/3]"
            >
              <img
                {...cardImgProps(ind.img)}
                alt={t(`industries.${ind.key}`)}
                className="absolute inset-0 w-full h-full object-cover opacity-75 transition-all duration-[1200ms] ease-out group-hover:opacity-95 group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 px-scrim-base" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <h3 className="px-display text-lg md:text-2xl font-semibold text-white leading-tight">
                  {t(`industries.${ind.key}`)}
                </h3>
                <p className="px-data text-[10px] tracking-[0.18em] uppercase text-white/55 mt-2 leading-snug">
                  {t(`industries.${ind.key}Note`)}
                </p>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
